import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { LayoutDashboard, Home, Tag, MessageCircle, LogOut, Link2, FolderLock } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { clientAuthConfig } from '../../utils/clientAuthConfig';
import { markResumeRecap } from '../../utils/chatResumeRecap';
import { resolveBuyerChatPath } from '../../utils/resolveBuyerChatPath';
import api from '../../services/api';
import LightSwitch, { readBuyerDaylight, writeBuyerDaylight } from '../LightSwitch';
import '../../pages/ClientPortal.css';

function NavBadge({ count }) {
  if (!count) return null;
  return <span className="client-nav-badge">{count > 99 ? '99+' : count}</span>;
}

export default function ClientPortalLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { client, loading, logout, hasToken, agentSlug } = useClientAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatPath, setChatPath] = useState(`/chat/${slug}`);
  const [chatIsResume, setChatIsResume] = useState(false);
  const [daylight, setDaylight] = useState(readBuyerDaylight);
  const nightClass = daylight ? '' : ' client-page--night';

  const toggleDaylight = () => {
    setDaylight((day) => {
      writeBuyerDaylight(!day);
      return !day;
    });
  };

  useEffect(() => {
    if (!loading && !client && !hasToken) {
      navigate(`/client/${slug}/auth?mode=login`, { replace: true });
    }
  }, [loading, client, hasToken, navigate, slug]);

  useEffect(() => {
    if (!agentSlug) return undefined;
    const load = () => {
      api.get('/clients/me/messages/unread-count', clientAuthConfig(agentSlug))
        .then((res) => setUnreadCount(res.data?.unread_count || 0))
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 30000);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', load);
    };
  }, [agentSlug]);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    resolveBuyerChatPath(slug).then(({ path, isResume }) => {
      if (!cancelled) {
        setChatPath(path);
        setChatIsResume(isResume);
      }
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading || !client) {
    return (
      <div className={`client-page client-page--portal${nightClass}`}>
        <div className="client-page__card client-page__card--wide">
          <div className="empty-state"><div className="spinner" /></div>
        </div>
      </div>
    );
  }

  const goToChat = () => {
    if (!chatIsResume) markResumeRecap(slug);
    navigate(chatPath);
  };

  const handleLogout = () => {
    logout(slug);
    navigate(`/chat/${slug}`);
  };

  const initials = `${client.first_name?.[0] || ''}${client.last_name?.[0] || ''}` || 'C';
  const showBuying = client.client_role === 'buyer' || client.client_role === 'both';
  const showSelling = client.client_role === 'seller' || client.client_role === 'both';

  return (
    <div className={`client-page client-page--portal-shell${nightClass}`}>
      <div className="client-shell">
        <aside className="client-sidebar">
          <div className="client-sidebar__logo">
            <h1>Agents<span>4</span>Agents</h1>
            <p>Your workspace with {client.agent?.first_name}</p>
          </div>

          <nav className="client-sidebar__nav">
            <NavLink to={`/client/${slug}`} end className={({ isActive }) => `client-nav-link${isActive ? ' active' : ''}`}>
              <LayoutDashboard size={16} />
              Overview
              <NavBadge count={unreadCount} />
            </NavLink>
            {showBuying && (
              <NavLink to={`/client/${slug}/buying`} className={({ isActive }) => `client-nav-link${isActive ? ' active' : ''}`}>
                <Home size={16} />
                Buying
              </NavLink>
            )}
            {showSelling && (
              <NavLink to={`/client/${slug}/selling`} className={({ isActive }) => `client-nav-link${isActive ? ' active' : ''}`}>
                <Tag size={16} />
                Selling
              </NavLink>
            )}
            <NavLink to={`/client/${slug}/resources`} className={({ isActive }) => `client-nav-link${isActive ? ' active' : ''}`}>
              <Link2 size={16} />
              Resources
            </NavLink>
            <button type="button" className="client-nav-link client-nav-link--button" onClick={goToChat}>
              <MessageCircle size={16} />
              Chat with {client.agent?.assistant_name || 'Ava'}
            </button>
            <span className="client-nav-link client-nav-link--disabled" title="Coming soon">
              <FolderLock size={16} />
              Document vault
              <span className="client-nav-soon">Soon</span>
            </span>
          </nav>

          <div className="client-sidebar__footer">
            <div className="client-sidebar__theme">
              <LightSwitch daylight={daylight} onToggle={toggleDaylight} />
            </div>
            <div className="client-sidebar__card">
              <div className="client-sidebar__avatar">{initials}</div>
              <div className="client-sidebar__info">
                <div className="client-sidebar__name">{client.first_name} {client.last_name}</div>
                <div className="client-sidebar__status">Active</div>
              </div>
              <button type="button" onClick={handleLogout} className="client-form__secondary client-sidebar__logout" title="Sign out">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </aside>

        <main className="client-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
